import * as popupAlignments from './alignments';

/*
 * functions used to return the top and left position based on
 * certain alignments. if `test` is not true, the functions will
 * return false if the balloon tip would not fit. if test is true
 * the positioning will be pushed so the balloon tip is visible inside
 * the iframe, but not pointing exactly where requested
 * (note this only pushes top/bottom alignment up or down, and left/right
 * alignment left or right. further translation of the balloon tip content is
 * done further down if parts are still sticking out)
 */
function getAlignment(
  alignment,
  { arrow = 6, target, size, spacing, margin, force }
) {
  let top, left;

  switch (alignment) {
    case popupAlignments.TOP:
      // center the balloon tip horizontally relative to the target element
      left = target.left + target.width / 2 - size.width / 2;
      top = target.top - size.height - arrow - spacing;

      if (top < margin) {
        if (!force) {
          return false;
        }

        top = margin;
      }

      return { top, left };

    case popupAlignments.BOTTOM:
      // center the balloon tip horizontally relative to the target element
      left = target.left + target.width / 2 - size.width / 2;
      // place the balloon tip under the target element
      top = target.top + target.height + spacing;

      if (window.innerHeight - (top + size.height + arrow) < margin) {
        if (!force) {
          return false;
        }

        top = window.innerHeight - (size.height + arrow + spacing + margin);
      }

      return { top, left };

    case popupAlignments.LEFT:
      left = target.left - size.width - spacing - arrow;
      // center the balloon tip vertically relative to the target element
      top = target.top + target.height / 2 - size.height / 2;

      if (left < margin) {
        if (!force) {
          return false;
        }

        left = margin;
      }

      return { top, left };

    case popupAlignments.RIGHT:
      // place the balloon tip to the rightof the target element
      left = target.left + target.width + spacing;
      // center the balloon tip vertically relative to the target element
      top = target.top + target.height / 2 - size.height / 2;

      if (window.innerWidth - (left + size.width + arrow) < margin) {
        if (!force) {
          return false;
        }

        left = window.innerWidth - (size.width + arrow + spacing + margin);
      }

      return { top, left };

    default:
      throw new Error(`Unknown alignment '${alignment}'`);
  }
}

function setPosition(size, target, options) {
  const margin = options.margin || 0;
  const spacing = options.spacing || 0;

  /*
   * the order which to try different alignments, depending on what
   * alignment was passed as option
   * FIXME shorter name
   */
  const defaultAlignmentAttemptSequence = {
    [popupAlignments.TOP]: [
      popupAlignments.TOP,
      popupAlignments.BOTTOM,
      popupAlignments.RIGHT,
      popupAlignments.LEFT,
    ],
    [popupAlignments.BOTTOM]: [
      popupAlignments.BOTTOM,
      popupAlignments.TOP,
      popupAlignments.RIGHT,
      popupAlignments.LEFT,
    ],
    [popupAlignments.LEFT]: [
      popupAlignments.LEFT,
      popupAlignments.RIGHT,
      popupAlignments.TOP,
      popupAlignments.BOTTOM,
    ],
    [popupAlignments.RIGHT]: [
      popupAlignments.RIGHT,
      popupAlignments.LEFT,
      popupAlignments.TOP,
      popupAlignments.BOTTOM,
    ],
  };

  const sequence = Array.isArray(options.alignment)
    ? options.alignment
    : defaultAlignmentAttemptSequence[options.alignment];

  /*
   * reducer that loops through the alignments and returns the first alignment and top/left
   * position where the balloon tip fits. it will return false if no alignments would fit,
   * and thus the destructuring leads to alignment, top and left being undefined
   */
  let { alignment, top, left } = sequence.reduce((result, alignment) => {
    if (result) {
      return result;
    }

    const position = getAlignment(alignment, {
      target,
      size,
      spacing,
      margin,
      force: false,
    });

    return position && Object.assign({ alignment }, position);
  }, false);

  if (!alignment) {
    /*
     * if alignment is not set, no alignment that fits has been found.
     * force alignment of requested `options.aligment`
     */
    ({ top, left } = getAlignment(options.alignment, {
      target,
      size,
      spacing,
      margin,
      force: true,
    }));

    alignment = options.alignment;
  }

  /*
   * `options.offset` is only used by the vertical measure input balloon tip... since it flips
   * using css transform it's visual position is the same as its DOM position.
   * TODO make this account for aligment fixes and such
   */
  if (options.offset) {
    left += options.offset.left || 0;
    top += options.offset.top || 0;
  }

  const adjust = {
    top: '',
    left: '',
  };

  /*
   * the following if/else statement translates the .content-holder element inside
   * the balloon tip if it is still outside the iframe. this leaves the arrow in place.
   * translates top/bottom alignment left and right, and left/right alignment
   * up and down.
   */
  if (
    alignment === popupAlignments.TOP ||
    alignment === popupAlignments.BOTTOM
  ) {
    if (left < margin) {
      adjust.left = margin - left;
    } else if (window.innerWidth - (left + size.width) < margin) {
      adjust.left = window.innerWidth - (left + size.width) - margin;
    }
  } else {
    if (top < margin) {
      adjust.top = margin - top;
    } else if (window.innerHeight - (top + size.height) < margin) {
      adjust.top = window.innerHeight - (top + size.height) - margin;
    }
  }

  return {
    alignment,
    left,
    top,
    adjust,
  };
}

export default setPosition;
